import { useEffect, useMemo, useState } from 'react';
import { PencilSquareIcon, TrashIcon, LockClosedIcon, TagIcon } from '@heroicons/react/24/outline';
import { PageHeader, Card } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import SportBadge from '@/components/ui/SportBadge';
import Dropdown from '@/components/ui/Dropdown';
import EmptyState from '@/components/ui/EmptyState';
import Pagination from '@/components/ui/Pagination';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import { BulkDeleteBar, SelectAllHeader, SelectRowCell } from '@/components/ui/BulkDelete';
import { useRowSelection } from '@/lib/useRowSelection';
import { useDebounce } from '@/lib/useDebounce';
import { deleteTheme as deleteThemeById, type Theme, type ThemePayload } from '@/api/themesService';
import { useThemes } from './useThemes';
import ThemeModal from './ThemeModal';
import ThemeImportModal from './ThemeImportModal';

const rowId = (t: Theme) => t.id;

const ThemesPage = () => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const filters = useMemo(() => ({ search: debouncedSearch || undefined, page }), [debouncedSearch, page]);
  const { items, total, pageSize, isLoading, createTheme, updateTheme, deleteTheme, isSaving, invalidate } =
    useThemes(filters);
  const selection = useRowSelection(items, rowId, filters);

  const [modalOpen, setModalOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState<Theme | null>(null);

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };
  const openEdit = (theme: Theme) => {
    setEditing(theme);
    setModalOpen(true);
  };

  const handleSubmit = (payload: ThemePayload) =>
    editing ? updateTheme({ id: editing.id, payload }) : createTheme(payload);

  const handleDelete = (theme: Theme) => {
    if (theme.sessionCount > 0) return;
    if (window.confirm(`Supprimer la thématique « ${theme.label} » ?`)) deleteTheme(theme.id);
  };

  return (
    <>
      <PageHeader
        title="Thématiques"
        description="Entrainement / Thèmes"
        action={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setImportOpen(true)}>
              Importer un CSV
            </Button>
            <Button onClick={openCreate}>+ Ajouter</Button>
          </div>
        }
      />

      <Card className="mb-4">
        <div className="w-72">
          <Input
            label="Rechercher un thème"
            placeholder="Libellé"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </Card>

      {isLoading && <p className="text-sm text-gray-500">Chargement…</p>}

      {!isLoading && items.length === 0 && (
        <EmptyState icon={<TagIcon />} title="Aucune thématique" description="Créez des thématiques de séances." />
      )}

      {!isLoading && items.length > 0 && (
        <Card className="p-0">
          <BulkDeleteBar
            selection={selection}
            labelOf={(t) => t.label}
            remove={(t) => deleteThemeById(t.id)}
            onDone={invalidate}
          />
          <Table>
            <THead>
              <tr>
                <SelectAllHeader selection={selection} />
                <TH>Libellé</TH>
                <TH>Description</TH>
                <TH>Sports</TH>
                <TH>Nbr séances</TH>
                <TH className="text-right">Actions</TH>
              </tr>
            </THead>
            <TBody>
              {items.map((theme) => (
                <TR key={theme.id}>
                  <SelectRowCell selection={selection} row={theme} />
                  <TD className="font-bold text-gray-900 dark:text-gray-100" title={theme.identifier}>
                    {theme.label}
                  </TD>
                  <TD className="max-w-xs truncate text-gray-500">{theme.description ?? '—'}</TD>
                  <TD>
                    <div className="flex flex-wrap gap-1">
                      {theme.sports.length === 0 ? (
                        <span className="text-gray-400">—</span>
                      ) : (
                        theme.sports.map((s) => (
                          <SportBadge identifier={s} label={s} />
                        ))
                      )}
                    </div>
                  </TD>
                  <TD>
                    {theme.sessionCount > 0 ? (
                      <span className="inline-flex items-center gap-1">
                        <LockClosedIcon
                          className="h-4 w-4 text-gray-400"
                          title={`Suppression impossible : ${theme.sessionCount} séance(s) utilisent cette thématique.`}
                        />
                        {theme.sessionCount}
                      </span>
                    ) : (
                      0
                    )}
                  </TD>
                  <TD className="text-right">
                    <Dropdown
                      actions={[
                        {
                          label: 'Modifier',
                          icon: <PencilSquareIcon className="h-4 w-4" />,
                          onClick: () => openEdit(theme),
                        },
                        {
                          label:
                            theme.sessionCount > 0 ? 'Utilisée — suppression bloquée' : 'Supprimer',
                          icon: <TrashIcon className="h-4 w-4" />,
                          destructive: true,
                          onClick: () => handleDelete(theme),
                        },
                      ]}
                    />
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
          <Pagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} />
        </Card>
      )}

      <ThemeModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
        theme={editing}
        isSaving={isSaving}
      />

      <ThemeImportModal open={importOpen} onClose={() => setImportOpen(false)} onImported={invalidate} />
    </>
  );
};

export default ThemesPage;
